import {DataItem, DisplayMode} from "@/models/general";
import {zeroCompensation} from "@/plugins/chart.plugins";
import {ChartLegendLabelItem, ChartTooltipItem} from "chart.js";
import {LABELS} from "@/consts/consts";

//const Chart = require("chart.js");

export const DataPointTypes = {
  CONV_REF_CATEGORY: 'CONV_REF_CATEGORY',
  CTR_REF_CATEGORY: 'CTR_REF_CATEGORY',
  IMP_REF_CATEGORY: 'IMP_REF_CATEGORY',
  CONS_PLAT_HOUR: 'CONS_PLAT_HOUR',
  CONS_PLAT_DAY: 'CONS_PLAT_DAY',
  IMPR_BY_PLATFORM: 'IMPR_BY_PLATFORM',
  CONV_BY_PLATFORM: 'CONV_BY_PLATFORM',
  CTR_PLATFORM: 'CTR_PLATFORM',
  CTR_USER_INTEREST: 'CTR_USER_INTEREST',
  CLICKS_USER_INTEREST: 'CLICKS_USER_INTEREST',
  CTR_IMPR_SUB_CATEGORY: 'CTR_IMPR_SUB_CATEGORY',
  CTR_IMPR_CHAR_COUNT: 'CTR_IMPR_CHAR_COUNT',
  CTR_KEYWORD: 'CTR_KEYWORD',
}

export const ChartNames = {
  CONV_REF_CATEGORY: 'CONV_REF_CATEGORY',
  CTR_REF_CATEGORY: 'CTR_REF_CATEGORY',
  IMP_REF_CATEGORY: 'IMP_REF_CATEGORY',
  CONS_PLAT_HOUR: 'CONS_PLAT_HOUR',
  CONS_PLAT_DAY: 'CONS_PLAT_DAY',
  IMPR_BY_PLATFORM: 'IMPR_BY_PLATFORM',
  CONV_BY_PLATFORM: 'CONV_BY_PLATFORM',
  CTR_PLATFORM: 'CTR_PLATFORM',
  CTR_USER_INTEREST: 'CTR_USER_INTEREST',
  CLICKS_USER_INTEREST: 'CLICKS_USER_INTEREST',
  CTR_IMPR_SUB_CATEGORY: 'CTR_IMPR_SUB_CATEGORY',
  CTR_IMPR_CHAR_COUNT: 'CTR_IMPR_CHAR_COUNT',
  CTR_IMPR_CHAR_COUNT_2: 'CTR_IMPR_CHAR_COUNT_2',
  CTR_KEYWORD: 'CTR_KEYWORD',
}

function percentageTooltips(type: string) {
  return {
    enabled: true,
    mode: 'single',
    callbacks: {
      title: (tooltipItem: any, data: any) => {
        return data.labels[tooltipItem[0].index] + ':'
      },
      label: function (tooltipItem: any, data: any) {
        //get the concerned dataset
        var dataset = data.datasets[tooltipItem.datasetIndex];

        //get the current items value
        var currentValue = dataset.data[tooltipItem.index];

        return ` ${type}: ${currentValue}%`;
      }
    }

  }
}

function clicksScore() {
  return {
    mode: 'index',
    callbacks: {
      title: (tooltipItem: ChartTooltipItem[], data: any) => {
       return tooltipItem[0].label
      },
      label: function (tooltipItem: any, data: any) {
        //get the concerned dataset
        var dataset = data.datasets[tooltipItem.datasetIndex];
        var label = data.datasets[tooltipItem.datasetIndex].label || '';
        //get the current items value
        var currentValue = dataset.data[tooltipItem.index];
        // <day-of-week> - <platform-name>:
        // Clicks Score: <value>

        const start = label.replace(LABELS.previousPeriod, '')
        const suffix = isPrevious(label) ? ' '+LABELS.previousPeriod : ''

        return `${start} Clicks${suffix}:  ${currentValue}`;
      }
    }
  }
}


const pieChartTolltipLabels = {
  callbacks: {
    // this callback is used to create the tooltip label
    label: function (tooltipItem: any, data: any) {
      // get the data label and data value to display
      // convert the data value to local string so it uses a comma seperated number
      var dataLabel = data.labels[tooltipItem.index];
      var value = ' : ' + data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toLocaleString() + '%'

      // make this isn't a multi-line label (e.g. [["label 1 - line 1, "line 2, ], [etc...]])
      if (dataLabel instanceof Array) {
        // show value on first line of multiline label
        // need to clone because we are changing the value
        dataLabel = dataLabel.slice();
        dataLabel[0] += value;
      } else {
        dataLabel += value;
      }

      // return the text to display on the tooltip
      return dataLabel;
    }
  }
}


const pieChartOptions = {
  tooltips: pieChartTolltipLabels,
  legend: {
    onClick: (e: Event) => e.stopPropagation(),
    position: 'left',
  },
  plugins: {
    labels: false
  }
}


export const CONTENT_PAIRING_SECTION: DataItem[] = [
  {
    title: 'Popular Content Pairing',
    tooltip: 'CTR compared to average for ads running on specific types of pages. Higher numbers indicate that consumers are more likely to click on these ads when they appear on a page with this topic.',
    chartType: ['horizontalBar'],
    columnHeaders: ['Content Category', '% Deviation from Average CTR'],
    chartName: ChartNames.CTR_REF_CATEGORY,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          ticks: {
            beginAtZero: true
          }
        }]
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem: any, data: any) => {
            return data.labels[tooltipItem[0].index]
          },
          label: function (tooltipItem: any, data: any) {
            const v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return ` CTR (compared to average): ${v}%`
          }
        }
      }
    }
  },
  {
    title: 'Conversions by Publisher Section ',
    tooltip: 'Percentage of conversions based on sources where ads appeared.',
    chartType: ['pie'],
    columnHeaders: ['Publisher Section', '% of Total Conversions'],
    chartName: ChartNames.CONV_REF_CATEGORY,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      tooltips: percentageTooltips('Conversion'),
      legend: {
        onClick: (e: Event) => e.stopPropagation(),
        position: 'left',
      },
      plugins: {
        labels: false
      }
    }
  },
  {
    title: 'Impressions by Publisher Section',
    columnHeaders: ['Publisher Section', '% of Total Impressions'],
    tooltip: 'Percentage of impressions based on sources where ads appeared.',
    chartType: ['pie'],
    chartName: ChartNames.IMP_REF_CATEGORY,
    data: [],
    displayMode: DisplayMode.Centered,
    chartOptions: {
      tooltips: percentageTooltips('Impressions'),
      legend: {
        onClick: (e: Event) => e.stopPropagation(),
        position: 'left',
      },
      plugins: {
        labels: false
      }
    }
  }
]

export const CONSUMER_INTERESTS: DataItem[] = [
  {
    title: 'Top Consumer Interests',
    tooltip: 'CTR compared to average based on consumer interests. Higher numbers indicate consumers with these interests are more likely to click on ads for this vertical and region.',
    chartType: ['horizontalBar'],
    columnHeaders: ['Interest Category', '% Deviation from Average CTR'
    ],
    chartName: ChartNames.CTR_USER_INTEREST,
    data: [],
    displayMode: DisplayMode.Centered,
    chartOptions: {
      legend: {
        display: false
      },
      tooltips: percentageTooltips('CTR (compared to average)'),
      scales: {
        xAxes: [{
          ticks: {
            beginAtZero: true
          }
        }]
      },
    }
  },
  {
    title: 'Click Distribution by Interests',
    tooltip: 'Percentage of clicks coming from specific consumer interests. Only interests with 5%+ are shown. “Average” indicates the average click percentage for an interest category.',
    chartType: ['horizontalBar'],
    columnHeaders: ['Interest Category', '% Clicks'],
    chartName: ChartNames.CLICKS_USER_INTEREST,
    data: [],
    displayMode: DisplayMode.Centered,
    chartOptions: {
      legend: {
        display: false
      }, tooltips: percentageTooltips('Clicks'),
      scales: {
        xAxes: [{
          ticks: {
            beginAtZero: true
          }
        }]
      },
    }
  },


]

export const CREATIVE_THEMES: DataItem[] = [
  {
    title: 'Available Content vs Clicks',
    tooltip: 'Supply vs demand - volume of category content based on network compared to CTR.' +
      ' Impressions are shown based on a scale of 0 to 100 where 100 stands for the highest Impressions measured.' +
      ' CTR is shown based on a scale of 0 to 100 where a value of 100 is given to the content category with highest CTR.',
    chartType: ['bar', 'scatter'],
    columnHeaders: ['Category, CTR Rank, Impressions Rank'],
    chartName: ChartNames.CTR_IMPR_SUB_CATEGORY,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: true,
        labels: {
          filter: function (item: ChartLegendLabelItem) {
            // Logic to remove a particular legend item goes here
            return item.text.toLowerCase().indexOf('previous') == -1;
          }
        },
        onClick: (e: any) => e.stopPropagation()

      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem: any, data: any) => {
            const v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            const label = data.datasets[tooltipItem.datasetIndex].label
            return `${label}: ${v}`;
          }
        }
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true
          },
          id: 'A',
          type: 'linear',
          position: 'right',
          display: false,

          scaleLabel: {
            display: false,
            labelString: "Impressions %",
            fontColor: "black",
          },
        }, {
          id: 'B',
          type: 'linear',
          position: 'left',
          scaleLabel: {
            display: true,
            labelString: "CTR",
            fontColor: "black",
          },
          ticks: {
            max: 100,
            min: 0
          }
        }]
      },
      plugins: {
        labels: false
      }
    },

  },
  {
    title: 'Impressions by Headline Length',
    tooltip: 'Impressions for headline character length ranges. Impressions are shown based on a scale of 0 to 100 where 100 stands for the highest Impressions measured.\n\nOptimize headline length for your campaigns based on popular character counts as well as performance (CTR). Make note of length differences on desktop and mobile.',
    chartType: ['bar', 'bar'],
    summaryIndex: 1,
    columnHeaders: ['Char Count', 'Impressions (Desktop)', 'Impressions (Mobile)'],
    chartName: ChartNames.CTR_IMPR_CHAR_COUNT,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: true,
        labels: {
          filter: function (item: any) {
            // Logic to remove a particular legend item goes here
            return !item.text.includes('previous');
          }
        }

      },
      tooltips: {
        callbacks: {
          label: (tooltipItem: any, data: any) => {
            const v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            let label = data.datasets[tooltipItem.datasetIndex].label
            if(label.includes('(previous')){
              label = label.replace(/\([^)]+\)/g, "");
            }
            return `${label} - Impressions: ${v}`
          }
        }
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true
          },
          scaleLabel: {
            display: false,
            fontColor: "black",
          },
        }]
      },
      plugins: {
        labels: false
      }
    },

  },
  {
    title: 'CTR by Headline Length',
    tooltip: 'CTR for headline character length ranges. CTR is shown based on a scale of 0 to 100. 100 is the highest CTR.  Optimize headline length for your campaigns based on popular character counts as well as performance (CTR). Make note of length differences on desktop and mobile.',
    chartType: ['bar', 'bar'],
    summaryIndex: 2,
    columnHeaders: ['Char Count', 'CTR (Desktop)', 'CTR (Mobile)'],
    chartName: ChartNames.CTR_IMPR_CHAR_COUNT_2,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: true,
        labels: {
          filter: function (item: any) {
            // Logic to remove a particular legend item goes here
            return !item.text.includes('(previous');
          }
        }
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem: any, data: any) => {
            const v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            let label = data.datasets[tooltipItem.datasetIndex].label
            if(label.includes('(previous')){
              label = label.replace(/\([^)]+\)/g, "");
            }
            return `${label} - CTR: ${v}`
          }
        }
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true
          },
          scaleLabel: {
            display: false,
            fontColor: "black",
          },
        }]
      },
      plugins: {
        labels: false
      }
    },

  },
  {
    title: 'Image Trends',
    tooltip: 'Image keywords with highest CTR. Image keywords are classified using AI vision software. The size of the word is proportional to the CTR – the bigger the word, the higher the CTR',
    chartType: ['cloud'],
    columnHeaders: ['Keyword', 'CTR Rank'],
    chartName: ChartNames.CTR_KEYWORD,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: false
      },
      plugins: {
        labels: false
      },
    }
  },
]

export const PLATFORM_ANALYSIS_SECTION: DataItem[] = [
  {
    title: 'Platform Engagement (CTR)',
    tooltip: 'Percentages represent the platform’s CTR compared to the region’s average CTR.',
    chartType: ['bar'],
    columnHeaders: ['Platform', '% Deviation from Average CTR'],
    chartName: ChartNames.CTR_PLATFORM,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {
      legend: {
        display: false
      },
      plugins: {
        labels: false
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem: any, data: any) => {
            return data.labels[tooltipItem[0].index]
          },
          label: function (tooltipItem: any, data: any) {
            const v = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return ` CTR (compared to average): ${v}%`
          }
        }
      }
    },
    cssClasses: ['horizental'],
    chartPlugins: [zeroCompensation]
  },
  {
    title: 'Conversion by Platform',
    tooltip: 'Percentage of conversions coming from each platform. ',
    chartType: ['doughnut'],
    columnHeaders: ['Platform', '% of Total Conversions'],
    chartName: ChartNames.CONV_BY_PLATFORM,
    displayMode: DisplayMode.Centered,
    data: [],
    cssClasses: ['short'],

    chartOptions: {
      legend: {
        display: true,
        onClick: (e: Event) => e.stopPropagation(),
      },

      cutoutPercentage: 60,
      // plugins: {
      //   labels: {
      //     render: 'label',
      //     position: 'outside',
      //     textMargin: 10
      //   }
      // },
      tooltips: percentageTooltips('Conversion')
    },
  }, {
    title: 'Impressions by Platform',
    tooltip: 'Percentage of Impressions coming from each platform. ',
    chartType: ['doughnut'],
    columnHeaders: ['Platform', '% of Total Impressions'],
    chartName: ChartNames.IMPR_BY_PLATFORM,
    displayMode: DisplayMode.Centered,
    data: [],
    cssClasses: ['short'],
    chartOptions: {
      legend: {
        display: true,
        onClick: (e: Event) => e.stopPropagation(),
      },
      cutoutPercentage: 60,
      tooltips: percentageTooltips('Impressions'),
    }
  },
  {
    title: 'Content Consumption by Weekday',
    tooltip: 'See what day and platform had the most clicks. Values are calculated based on a scale from 0 to 100. 100 is the day and platform with the most clicks as a fraction of total clicks.' +
      ' You may click on the legend label to remove/add that platform from the chart.',
    chartType: ['line'],
    columnHeaders: ['Day of Week', '% of Total Clicks (Desktop)', '% of Total Clicks (Tablet)', '% of Total Clicks (Mobile)'],
    chartName: ChartNames.CONS_PLAT_DAY,
    data: [],
    displayMode: DisplayMode.FullRow,
    chartOptions: {

      legend: {
        onClick: (e: Event, legendItem: any) => {
          //due to compare feture we decided to disable filter by platform
          //TODO need to find a way to filter by platform for both current and prev datasets
          e.stopPropagation();
        },
        labels: {
          filter: function (item: any) {
            // Logic to remove a particular legend item goes here
            return !item.text.includes('previous');
          }
        },
      },
      tooltips: clicksScore()
    }
  },
  {
    title: 'Content Consumption by Time of Day',
    tooltip: 'See what time of day and platform had the most clicks. Values are calculated based on a scale from 0 to 100. 100 is the time and platform with the most clicks as a fraction of total clicks.' +
      ' You may click on the legend label to remove/add that platform from the chart.',
    chartType: ['line'],
    columnHeaders: ['Hour of Day', '% of Total Clicks (Desktop)', '% of Total Clicks (Tablet)', '% of Total Clicks (Mobile)'],
    data: [],
    displayMode: DisplayMode.FullRow,
    chartName: ChartNames.CONS_PLAT_HOUR,
    chartOptions: {
      legend: {
        display: true,
        onClick: (e: Event) => e.stopPropagation(),
        labels: {
          filter: function (item: any) {
            // Logic to remove a particular legend item goes here
            return !item.text.includes('prev');
          }
        }
      },
      tooltips: clicksScore()
    }
  }

]

function isPrevious(text: string){
  return text.toLowerCase().indexOf('previous') !== -1
}
